import {
  useState,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useNavigate } from "react-router-dom";
import { Dropdown } from "react-bootstrap";
import DocumentUpload from "./documentUpload/DocumentUpload";
import PatientDetail from "./patientDetail/PatientDetail";
import IntakeSheet from "./intakeSheet/IntakeSheet";
import MedicalNotes from "./medicalNote/medicalNote";
import "../styles/PatientPortal.css";
import toast from "react-hot-toast";
import { GetPatientDetails } from "./patientDetail/Service";
import { CallApiForGetCaseDocuments } from "../helpers/http";
import CompletionScreen from "./completionScreen/CompletionScreen";
import { usePatientData } from "../commonFunction/storage/Patientdatacontext";
import { getSessionData } from "../helpers/Util";
import RateReviewModal from "./rateUs/RateReviewModal";
import { getReviewEventIds } from "./rateUs/RateUsStep";


/* ── Design tokens ── */
const C = {
  bg: "#f4efe8",
  cardBg: "#ffffff",
  s50: "#faf7f2",
  s100: "#f0e9de",
  s200: "#e2d5c3",
  s300: "#c9b89e",
  t400: "#2a9d8f",
  t500: "#1f8a7e",
  t600: "#176b62",
  t700: "#0f4e47",
  coral: "#e07b54",
  coralBg: "#fdf1ec",
  dk: "#1a1410",
  mid: "#3d332a",
  muted: "#7d6e61",
  light: "#b0a090",
  border: "#e5ddd3",
  ibg: "#fdfbf8",
  white: "#ffffff",
  glow: "rgba(42,157,143,0.18)",
};

/* ── Window width hook ── */
function useWindowWidth() {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handler, { passive: true });
    return () => window.removeEventListener('resize', handler);
  }, []);
  return width;
}

/* ── Steps config ── */
// Note: ACTIVE_STEPS are built dynamically inside the component
const STEP_DESCS = {
  patient: "Verify your identity and contact information carefully.",
  intake: "Complete all intake sheets — accident, insurance, authorization and referral.",
  documents: "Upload and manage your documents securely.",
};

/* ── Logo ── */
function Logo() {
  return (
    <svg
      width="32"
      height="32"
      viewBox="0 0 32 32"
      fill="none"
      style={{ flexShrink: 0 }}
    >
      <rect width="32" height="32" rx="8" fill={C.t500} />
      <circle
        cx="16"
        cy="16"
        r="9"
        stroke="white"
        strokeWidth="1.4"
        fill="none"
      />
      <line x1="16" y1="7" x2="16" y2="25" stroke="white" strokeWidth="1.4" />
      <line x1="7" y1="16" x2="25" y2="16" stroke="white" strokeWidth="1.4" />
      <path
        d="M9 12 Q16 9.5 23 12"
        stroke="white"
        strokeWidth="1.4"
        fill="none"
      />
      <path
        d="M9 20 Q16 22.5 23 20"
        stroke="white"
        strokeWidth="1.4"
        fill="none"
      />
    </svg>
  );
}

function computePatientPending(details) {
  if (!details) return true;
  const first = (details.patientFirstName || "").trim();
  const last = (details.patientLastName || "").trim();
  return !first || !last;
}

function computeDocsPending(uploadedMap, tokenDocuments) {
  if (!tokenDocuments?.length) return false;
  return tokenDocuments.some(
    (doc) => !uploadedMap[String(doc.DocumentId ?? doc.documentId)],
  );
}

function computeIntakePendingInitial(intakeSheetData) {
  if (!intakeSheetData || !Array.isArray(intakeSheetData)) return true;
  const allSaved = intakeSheetData.every(
    (s) => s.IsSaved === true || s.isSaved === true,
  );
  return !allSaved;
}

const isReviewRequestLink = () => {
  const td = getSessionData("tokenData", {}) || {};
  const raw = td.eventIds ?? td.EventIds;
  return Array.isArray(raw) ? raw.length > 0 : typeof raw === "string" && raw.trim() !== "";
};

function ScrollableStepTabs({
  steps,
  step,
  setStep,
  stepPendingMap,
  getStepSubLabel,
  getStepSubColor,
}) {
  const scrollRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 2);
    setCanRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 2);
  };

  const setRef = (el) => {
    scrollRef.current = el;
    if (el) {
      el.addEventListener("scroll", checkScroll, { passive: true });
      setTimeout(checkScroll, 0);
    }
  };

  useEffect(() => {
    const onResize = () => checkScroll();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const scroll = (dir) =>
    scrollRef.current?.scrollBy({ left: dir * 180, behavior: "smooth" });

  const ArrowBtn = ({ dir }) => {
    const visible = dir === -1 ? canLeft : canRight;
    return (
      <button
        onClick={() => scroll(dir)}
        className={`pp-nav-arrow pp-nav-arrow--${dir === -1 ? "left" : "right"}${!visible ? " pp-nav-arrow--hidden" : ""}`}
        aria-hidden={!visible}
        tabIndex={visible ? 0 : -1}
      >
        <svg
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--pp-t600)"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          {dir === -1 ? (
            <polyline points="15 18 9 12 15 6" />
          ) : (
            <polyline points="9 18 15 12 9 6" />
          )}
        </svg>
      </button>
    );
  };

  return (
    <div className="pp-nav-tabs-wrapper">
      <ArrowBtn dir={-1} />
      <nav ref={setRef} className="pp-nav-tabs" onScroll={checkScroll}>
        {steps.map((s, i) => {
          const done = i < step;
          const active = i === step;
          const hasPending = stepPendingMap[s.originalIndex ?? i] ?? false;
          const subLabel = getStepSubLabel(i, done, active);
          const subColor = getStepSubColor(i, done, active);
          return (
            <div key={s.id} style={{ display: "flex", alignItems: "center" }}>
              <button
                onClick={() => setStep(i)}
                className={`pp-stab${active ? " pp-stab--active" : ""}`}
                style={{ color: active ? C.t600 : done ? C.t500 : C.muted }}
              >
                <div
                  className={`pp-stab__num${done && !hasPending
                      ? " pp-stab__num--done"
                      : active || (done && hasPending)
                        ? " pp-stab__num--active"
                        : hasPending
                          ? " pp-stab__num--active"
                          : " pp-stab__num--inactive"
                    }`}
                >
                  {done && !hasPending ? (
                    <svg width="9" height="9" viewBox="0 0 12 12" fill="none">
                      <polyline
                        points="2,6 5,9 10,3"
                        stroke="white"
                        strokeWidth="2.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : (
                    s.num
                  )}
                </div>
                <div>
                  <div
                    className="pp-stab__label"
                    style={{ fontWeight: active ? 700 : 500 }}
                  >
                    {s.label}
                  </div>
                  <div className="pp-stab__sub" style={{ color: subColor }}>
                    {subLabel}
                  </div>
                </div>
              </button>
              {i < steps.length - 1 && (
                <svg
                  className="pp-stab__arrow"
                  width="10"
                  height="10"
                  viewBox="0 0 10 10"
                  fill="none"
                >
                  <path
                    d="M3 2l4 3-4 3"
                    stroke={C.s300}
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </div>
          );
        })}
      </nav>
      <ArrowBtn dir={1} />
    </div>
  );
}

export default function PatientPortal() {

  const { patientData, fetchPatientData } = usePatientData();
  const patientDetailRef = useRef(null);
  const intakeSheetRef = useRef(null);
  const docUploadRef = useRef(null);
  const medicalNotesRef = useRef(null);

  const [mountedSteps, setMountedSteps] = useState({ 0: true, 1: true });

  const tokenData = getSessionData("tokenData");

  const windowWidth = useWindowWidth();
  const showMobileBars = windowWidth <= 850;
  const showPatientNameInTopbar = windowWidth > 720;

  const doctorName = tokenData?.doctorName || "";
  const specialtyName = tokenData?.doctorSpecialtyName;
  const providerName = tokenData?.providerName || "";
  // const providerName = providerRaw.includes("-")
  //   ? providerRaw.split("-")[0].trim()
  //   : providerRaw;
  const locationLabel = [doctorName, specialtyName, providerName]
    .filter(Boolean)
    .join(" · ");

  const [step, setStep] = useState(0);
  const [uploadedDocs, setUploadedDocs] = useState({});
  const [copySignature, setCopySignature] = useState(false);

  const [patientHasPending, setPatientHasPending] = useState(true);
  const [intakeHasPending, setIntakeHasPending] = useState(true);
  const [docsHasPending, setDocsHasPending] = useState(true);
  const [patientName, setPatientName] = useState('');
  const [patientFirstName, setPatientFirstName] = useState('');
  const [patientNameInitials, setPatientnameInitials] = useState('');

  // ── TAB-SWITCH: when set, handleStepSuccess jumps to this step instead
  // of advancing to step+1. Null means normal Save & Continue behaviour.
  const tabSwitchTargetRef = useRef(null);

  // ── FINISH-GUARD: only show CompletionScreen when the Finish button was
  // explicitly clicked — not when a tab-switch triggers saveAndFinish.
  const isFinishActionRef = useRef(false);

  const tokenDocumentsFromStorage = JSON.parse(
    sessionStorage.getItem("tokenDocuments") || "[]",
  );
  const tokenIntakeSheetsFromStorage = JSON.parse(
    sessionStorage.getItem("tokenIntakeSheets") || "[]",
  );

  const tokenDocuments = Array.isArray(tokenData?.documents)
    ? tokenData.documents
    : (Array.isArray(tokenDocumentsFromStorage) ? tokenDocumentsFromStorage : []);

  const tokenIntakeSheets = Array.isArray(tokenData?.intakeSheets)
    ? tokenData.intakeSheets
    : (Array.isArray(tokenIntakeSheetsFromStorage) ? tokenIntakeSheetsFromStorage : []);

  const [intakeTabInfo, setIntakeTabInfo] = useState(() => ({
    isLastTab: tokenIntakeSheets.length <= 1,
    isOnlyTab: tokenIntakeSheets.length <= 1,
    totalTabs: tokenIntakeSheets.length,
    currentIndex: 0,
  }));

  const caseId = tokenData?.caseId || tokenData?.CaseId || "";
  const companyId = tokenData?.companyId || tokenData?.CompanyId || "";

  // ── Build Dynamic Steps ──
  const ACTIVE_STEPS = [
    { id: "patient", label: "Patient Details", originalIndex: 0, num: "01" },
  ];

  if (tokenIntakeSheets.length > 0) {
    ACTIVE_STEPS.push({
      id: "intake",
      label: "Intake Sheets",
      originalIndex: 1,
      num: String(ACTIVE_STEPS.length + 1).padStart(2, "0"),
    });
  }

  if (tokenDocuments.length > 0) {
    ACTIVE_STEPS.push({
      id: "documents",
      label: "Documents",
      originalIndex: 2,
      num: String(ACTIVE_STEPS.length + 1).padStart(2, "0"),
    });
  }

  const currentStepObj = ACTIVE_STEPS[step] || ACTIVE_STEPS[0];
  const currentStepId = currentStepObj.id;
  const currentOriginalIndex = currentStepObj.originalIndex;

  /* ── true when intake sheet or documents step is active (fill full screen) ── */
  const isFullHeightStep = currentStepId === "intake" || currentStepId === "documents";

  const [allDone, setAllDone] = useState(false);

//   const [showRateReview, setShowRateReview] = useState(
//   () => isReviewRequestLink() && !sessionStorage.getItem("rateReviewAutoOpened"),
// );



const [showRateReview, setShowRateReview] = useState(
  () => getReviewEventIds().length > 0 && !sessionStorage.getItem("rateReviewAutoOpened"),
);
useEffect(() => {
  if (showRateReview) sessionStorage.setItem("rateReviewAutoOpened", "1");
}, []); // eslint-disable-line

  useEffect(() => {
    getPatientInfo()
  }, [patientData]);

  const getPatientInfo = async (caseId, companyId) => {
    const patientInfo = patientData || await fetchPatientData(tokenData?.caseId, tokenData?.companyId);
    setPatientName(patientInfo ? `${patientInfo?.patientFirstName} ${patientInfo?.patientLastName}` : 'User');
    const initials = `${patientInfo?.patientFirstName[0] || ""}${patientInfo?.patientLastName[0] || ""}`.toUpperCase() || "?";
    setPatientnameInitials(initials);
    setPatientFirstName(patientInfo?.patientFirstName || '');
  }

  useEffect(() => {
    if (!caseId || !companyId) return;

    const checkAllSteps = async () => {
      await Promise.allSettled([
        (async () => {
          try {
            const res = await GetPatientDetails(caseId, companyId);
            if (res?.success && res?.data) {
              setPatientHasPending(computePatientPending(res.data));
            }
          } catch {
            /* leave as pending */
          }
        })(),

        (async () => {
          try {
            const raw = sessionStorage.getItem("tokenIntakeSheets");
            if (!raw) {
              setIntakeHasPending(true);
              return;
            }
            const sheets = JSON.parse(raw);
            const pending = computeIntakePendingInitial(sheets);
            setIntakeHasPending(pending);
          } catch {
            setIntakeHasPending(true);
          }
        })(),

        (async () => {
          try {
            const res = await CallApiForGetCaseDocuments(caseId);
            if (!res?.success || !Array.isArray(res.data)) {
              setDocsHasPending(tokenDocuments.length > 0);
              return;
            }
            const uploadedMap = {};
            res.data.forEach((doc) => {
              const key = String(doc.documentType ?? doc.documentId ?? doc.nodeId);
              uploadedMap[key] = true;
            });
            setDocsHasPending(computeDocsPending(uploadedMap, tokenDocuments));
          } catch {
            setDocsHasPending(tokenDocuments.length > 0);
          }
        })(),
      ]);
    };

    checkAllSteps();
  }, [caseId, companyId]);

  useEffect(() => {
    setMountedSteps((prev) => ({ ...prev, [currentOriginalIndex]: true }));
  }, [currentOriginalIndex]);

  const stepPendingMap = {
    0: patientHasPending,
    1: intakeHasPending,
    2: docsHasPending,
  };

  const completedCount = ACTIVE_STEPS.filter((s, i) => {
    if (stepPendingMap[s.originalIndex] !== undefined) return !stepPendingMap[s.originalIndex];
    return step > i;
  }).length;

  const pct = Math.round((completedCount / ACTIVE_STEPS.length) * 100);

  const handleStepSuccess = () => {
    // TAB-SWITCH: if a target step is queued, jump there instead of advancing.
    if (tabSwitchTargetRef.current !== null) {
      const target = tabSwitchTargetRef.current;
      tabSwitchTargetRef.current = null;
      setStep(target);
      return;
    }
    if (step < ACTIVE_STEPS.length - 1) {
      setStep((s) => s + 1);
    } else {
      // Only show CompletionScreen when the Finish button was explicitly clicked and >= 2 steps exist.
      if (isFinishActionRef.current && ACTIVE_STEPS.length >= 2) {
        setAllDone(true);
      }
      isFinishActionRef.current = false; // always reset
    }
  };

  const handleGoToDashboard = () => {
    setAllDone(false);
    setStep(0);
  };

  const handleGoToStep = (stepIndex) => {
    setAllDone(false);
    setStep(stepIndex);
  };

  const handlePatientReady = () => {
    const pending = patientDetailRef.current?.isPending?.() ?? false;
    setPatientHasPending(pending);
  };

  const handleSaveAndContinue = () => {
    const isLastPortalStep = step === ACTIVE_STEPS.length - 1;
    const isIntakeLastOrOnlyTab = intakeTabInfo.isLastTab || intakeTabInfo.isOnlyTab;

    if (isLastPortalStep && ACTIVE_STEPS.length >= 2) {
      if (currentStepId === "intake") {
        isFinishActionRef.current = isIntakeLastOrOnlyTab;
      } else {
        isFinishActionRef.current = true;
      }
    } else {
      isFinishActionRef.current = false;
    }

    if (currentStepId === "patient") patientDetailRef.current?.submit();
    else if (currentStepId === "intake") intakeSheetRef.current?.submit();
    else if (currentStepId === "documents") {
      docUploadRef.current?.submit();
    }
  };

  // ── TAB-SWITCH: clicking a tab runs the same validate+save as Save & Continue
  // for all steps. Validation runs, errors are shown, and navigation is blocked
  // if the form is invalid. Only navigates when onApiSuccess fires.
  // NOTE: isFinishActionRef is intentionally NOT set here, so tab-switching
  // from step 3 will save but will NOT trigger the CompletionScreen.
  const handleStepChange = (targetStep) => {
    if (targetStep === step) return;
    tabSwitchTargetRef.current = targetStep;

    if (currentStepId === "patient") patientDetailRef.current?.submit();
    else if (currentStepId === "intake") intakeSheetRef.current?.submit({ skipTabAdvance: true });
    else if (currentStepId === "documents") docUploadRef.current?.submit();
    else tabSwitchTargetRef.current = null;
  };

  const handleLogout = () => {
    localStorage.removeItem("userData");
    localStorage.removeItem("patientDetails");
    sessionStorage.clear();
    const tokenUrl = localStorage.getItem("tokenUrl");
    window.location.href = tokenUrl || "/login";
  };

  const getStepSubLabel = (i, done, active) => {
    if (active) return "In progress";
    const originalIdx = ACTIVE_STEPS[i]?.originalIndex;
    if (originalIdx !== undefined && stepPendingMap[originalIdx] !== undefined)
      return stepPendingMap[originalIdx] ? "Pending" : "Completed";
    return done ? "Completed" : "Pending";
  };

  const getStepSubColor = (i, done, active) => {
    if (active) return C.t500;
    const originalIdx = ACTIVE_STEPS[i]?.originalIndex;
    if (originalIdx !== undefined && stepPendingMap[originalIdx] !== undefined)
      return stepPendingMap[originalIdx] ? "#c2610c" : C.t400;
    return done ? C.t400 : "#c2610c";
  };

  return (
    <>

      {allDone && ACTIVE_STEPS.length >= 2 && (
        <CompletionScreen
          firstName={patientFirstName}
          onGoToDashboard={handleGoToDashboard}
          onLogout={handleLogout}
          onGoToStep={handleGoToStep}
          onClose={() => setAllDone(false)}
          availableSteps={ACTIVE_STEPS}
          stepStatus={{
            patientHasPending,
            intakeHasPending,
            docsHasPending,
          }}
        />
      )}

      <div
        style={{
          height: "100vh",
          background: C.bg,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* ══ Header ══ */}
        <header className="pp-header">
          {/* ── Top bar ── */}
          <div className="pp-topbar">
            <div className="pp-topbar__left">
              <Logo />
              <div className="pp-brand">
                <span className="pp-brand__name">Patient Portal</span>
                <span className="pp-brand__sub">BY GREEN BILLS</span>
              </div>

              {showRateReview && <RateReviewModal onClose={() => setShowRateReview(false)} />}

              {!showMobileBars && (
                <>
                  <div className="pp-user-pill" style={{ cursor: "default" }}>
                    <div className="pp-user-pill__avatar">{patientNameInitials}</div>
                    <div className="pp-user-pill__name">
                      <div className="pp-user-pill__welcome">Welcome</div>
                      <div className="pp-user-pill__fullname">{patientName}</div>
                    </div>
                  </div>

                  <div
                    style={{ position: "relative", display: "inline-block" }}
                    className="pp-loc-tag-wrap"
                  >
                    <span
                      className="pp-tag pp-tag--teal"
                      style={{
                        cursor: "default",
                        display: "inline-block",
                        verticalAlign: "middle",
                      }}
                    >
                      {locationLabel || "No location assigned"}
                    </span>
                    <div className="pp-loc-tooltip">
                      {[doctorName, specialtyName, providerName]
                        .filter(Boolean)
                        .join(" · ")}
                    </div>
                  </div>
                </>
              )}
              
              {showMobileBars && (
                <div className="pp-mobile-icons">
                  <Dropdown>
                    <Dropdown.Toggle as="div" className="pp-mobile-icon-wrapper" style={{ cursor: "pointer" }}>
                      <button type="button" className="pp-mobile-icon-toggle">
                        {patientNameInitials}
                      </button>
                    </Dropdown.Toggle>
                    <Dropdown.Menu align="end" style={{ borderRadius: '9px', padding: '6px', border: '1px solid #e5ddd3', boxShadow: '0 4px 15px rgba(0,0,0,0.08)', minWidth: '180px' }}>
                      <div style={{ padding: '8px 10px', borderBottom: '1px solid #f0e9de', marginBottom: '6px' }}>
                        <div style={{ fontWeight: 700, fontSize: '13px', color: '#1a1410', marginBottom: '4px' }}>{patientName}</div>
                        <div style={{ fontSize: '11px', color: '#7d6e61', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                          </svg>
                          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{locationLabel || "No location assigned"}</span>
                        </div>
                      </div>
                      <Dropdown.Item onClick={handleLogout} style={{ color: '#ef4444', fontWeight: 600, fontSize: '13px', padding: '8px 10px', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                          <polyline points="16 17 21 12 16 7" />
                          <line x1="21" y1="12" x2="9" y2="12" />
                        </svg>
                        Logout
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              )}
            </div>

            <div className="pp-topbar__right">
              {ACTIVE_STEPS.length > 1 && (
              <div className="pp-progress-pill">
                <div
                  className="pp-progress-ring"
                  style={{
                    background: `conic-gradient(${C.t400} ${pct * 3.6}deg, ${C.s100} 0)`,
                  }}
                >
                  <div className="pp-progress-ring__inner">
                    <span className="pp-progress-ring__pct">{pct}%</span>
                  </div>
                </div>
                <div>
                  <div className="pp-progress-label">Progress</div>
                  <div className="pp-progress-step">
                    Step {step + 1} / {ACTIVE_STEPS.length}
                    </div>
                  </div>
                </div>
              )}

              <button
                className="pp-logout-btn pp-btn-ghost pp-btn-logout"
                onClick={handleLogout}
              >
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
                <span>Logout</span>
              </button>
            </div>
          </div>


          {/* ── Step tab row — auto-saves before switching ── */}
          {ACTIVE_STEPS.length > 1 && (
            <ScrollableStepTabs
              steps={ACTIVE_STEPS}
              step={step}
              setStep={handleStepChange}
              stepPendingMap={stepPendingMap}
              getStepSubLabel={getStepSubLabel}
              getStepSubColor={getStepSubColor}
            />
          )}
        </header>

        <main className={`pp-main${isFullHeightStep ? " pp-main--intake" : ""}`}>
          {/* ── Page heading ── */}
          <div className="pp-page-heading">
            <div className="pp-page-heading__desc-row">
              <div className="pp-page-heading__desc">
                {STEP_DESCS[currentStepId]}
                {ACTIVE_STEPS.length > 1 && (
                  <span
                    className="pp-tag pp-tag--teal"
                    style={{ marginLeft: 10, verticalAlign: "middle" }}
                  >
                    Step {step + 1} / {ACTIVE_STEPS.length}
                  </span>
                )}
              </div>
              {currentStepId === "intake" && (
                <label className="pp-copy-sign-check">
                  <input
                    type="checkbox"
                    checked={copySignature}
                    onChange={(e) => {
                      if (e.target.checked) {
                        const hasSig =
                          intakeSheetRef.current?.hasSignature?.() ?? false;
                        if (!hasSig) {
                          toast.error(
                            "Signature is required.",
                          );
                          return;
                        }
                      }
                      setCopySignature(e.target.checked);
                    }}
                  />
                  <span className="pp-copy-sign-check__box">
                    {copySignature && (
                      <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                        <polyline
                          points="2,6 5,9 10,3"
                          stroke="white"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </span>
                  <span className="pp-copy-sign-check__label">
                    Apply signature
                  </span>
                </label>
              )}
            </div>
          </div>

          <div className={`pp-card${isFullHeightStep ? " pp-card--intake" : ""}`}>
            <div className="pp-card__top-bar" />

            <div
              className={`pp-anim-step${isFullHeightStep ? " pp-anim-step--intake" : ""}`}
            >
              {/* Step 0 — Patient Details */}
              <div style={{ display: currentStepId === "patient" ? "block" : "none" }}>
                {mountedSteps[0] && (
                  <PatientDetail
                    ref={patientDetailRef}
                    onApiSuccess={handleStepSuccess}
                    onReady={handlePatientReady}
                  />
                )}
              </div>

              {/* Step 1 — Intake Sheets (needs internal scroll) */}
              <div
                style={{
                  display: currentStepId === "intake" ? "flex" : "none",
                  flexDirection: "column",
                  flex: 1,
                  minHeight: 0,
                  height: "100%",
                }}
              >
                {mountedSteps[1] && (
                  <IntakeSheet
                    ref={intakeSheetRef}
                    onApiSuccess={handleStepSuccess}
                    copySignature={copySignature}
                    onPendingChange={setIntakeHasPending}
                    onTabInfoChange={setIntakeTabInfo}
                  />
                )}
              </div>

              {/* Step 2 — Documents */}
              <div
                style={{
                  display: currentStepId === "documents" ? "flex" : "none",
                  flexDirection: "column",
                  flex: 1,
                  minHeight: 0,
                  height: "100%",
                }}
              >
                {mountedSteps[2] && (
                  <DocumentUpload
                    ref={docUploadRef}
                    nodes={tokenDocuments}
                    onApiSuccess={handleStepSuccess}
                    uploadedDocs={uploadedDocs}
                    onUploadedDocsChange={setUploadedDocs}
                    onPendingChange={setDocsHasPending}
                  />
                )}
              </div>

              {/* Step 3 — Medical Notes / Questionnaire */}
              {/* <div style={{ display: step === 3 ? "block" : "none" }}>
                {mountedSteps[3] && (
                  <MedicalNotes
                    ref={medicalNotesRef}
                    onApiSuccess={handleStepSuccess}
                  />
                )}
              </div> */}
            </div>
            {/* end pp-anim-step */}

            {/* ── Footer ── always visible, never scrolls away ── */}
            <div className="pp-card-footer" style={{ flexShrink: 0 }}>
              <p className="pp-card-footer__security">
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={C.t400}
                  strokeWidth="2.5"
                  style={{ verticalAlign: "-2px", marginRight: 4 }}
                >
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
                Your Information is Encrypted &amp; Secure
              </p>
              <div className="pp-card-footer__btns">
                {step > 0 && (
                  <button
                    className="pp-btn-back"
                    onClick={() => setStep((s) => s - 1)}
                  >
                    ← Back
                  </button>
                )}
                <button className="pp-btn-save" onClick={handleSaveAndContinue}>
                  {(() => {
                    const isLastPortalStep = step === ACTIVE_STEPS.length - 1;
                    const isIntakeLastOrOnlyTab = intakeTabInfo.isLastTab || intakeTabInfo.isOnlyTab;
                    if (currentStepId === "intake") {
                      if (isLastPortalStep) {
                        return isIntakeLastOrOnlyTab ? "Finish" : "Save & Continue →";
                      }
                      return "Save & Continue →";
                    }
                    if (isLastPortalStep) {
                      return ACTIVE_STEPS.length === 1 ? "Update" : "Finish";
                    }
                    return "Save & Continue →";
                  })()}
                </button>
              </div>
            </div>
          </div>
          {/* end pp-card */}
        </main>
      </div>
    </>
  );
}